import { Toggle, Typography } from '@packages/ui/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5DtcFilter, Ssm5DtcFilterKey } from '../helpers/dtc-filters';

/** Props for {@link Ssm5DtcFilterSwitch}. */
export type Ssm5DtcFilterSwitchProps = {
    /** The filter the switch turns on and off. */
    filter: Ssm5DtcFilter;
    /** Whether the filter is switched on. */
    isChecked: boolean;
    /** Whether the switch can be used; a disabled switch shows as switched off. */
    isEnabled: boolean;
    /** Receives the filter and whether it is switched on once the switch is pressed. */
    onChange: (key: Ssm5DtcFilterKey, isChecked: boolean) => void;
};

const useStyles = createUseStyles({
    filterSwitch: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
    },
});

/**
 * A switch of the DTC panel filters, next to its label.
 *
 * @param props - {@link Ssm5DtcFilterSwitchProps}
 * @returns the switch
 */
export function Ssm5DtcFilterSwitch({ filter, isChecked, isEnabled, onChange }: Ssm5DtcFilterSwitchProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const label = t(filter.label);
    const change = useCallback((checked: boolean) => onChange(filter.key, checked), [filter.key, onChange]);

    return (
        <div className={classes.filterSwitch}>
            <Toggle
                accessibilityLabel={label}
                checked={isEnabled && isChecked}
                disabled={!isEnabled}
                onChange={change}
                size="small"
            />
            <Typography color={isEnabled ? 'textDarkPrimary' : 'brandDisabled'} variant="caption">
                {label}
            </Typography>
        </div>
    );
}
